// stripAnchors collapses inline <a href="#NODE_ID">label</a> markup to its
// plain label text. Anchors are a referential trace artifact (each mirrored in
// node.refs[]): useful to the engine, never to the founder reading the prose.
// The engine may write one into any string field of a node (the tree-wide
// anchor_ref_mirroring validator scans every string field), so node prose the
// founder app renders for reading passes through this one function. Never the
// founder's own words, an editable value or a pick's fill: those reach the
// engine verbatim.
//
// One exception, a display rule (0169; Simon's ruling of 2026-10-13: the root reads as the product's name, not as
// the idea sentence): given rootName, an anchor to the root whose label is the whole idea reads as the product's
// name. Nothing stored changes, and without rootName the output is the label, as before (rootNameOf,
// lib/displayName.ts, says when a product has a name to show).
export type RootName = { idea: string; name: string };

const ANCHOR_RE = /<a\s+([^>]*)>(.*?)<\/a>/g;
// The root's href exactly as the engine writes it (models.AnchorOpenTagPattern).
const ROOT_HREF_RE = /(?:^|\s)href="#root"(?:\s|$)/;
// The idea's last mark, when the anchor ends its sentence (the field ends, or a
// new sentence or paragraph starts), was that sentence's own; the name keeps it.
const END_MARK_RE = /[.!?]$/;
const ENDS_SENTENCE_RE = /^(?:$|\s+\p{Lu})/u;

export function stripAnchors(s: string, rootName?: RootName): string {
  return s.replace(ANCHOR_RE, (whole: string, attrs: string, label: string, offset: number, str: string) => {
    if (rootName === undefined || !ROOT_HREF_RE.test(attrs) || label.trim() !== rootName.idea) return label;
    const keepsMark =
      END_MARK_RE.test(rootName.idea) &&
      !END_MARK_RE.test(rootName.name) &&
      ENDS_SENTENCE_RE.test(str.slice(offset + whole.length));
    return keepsMark ? rootName.name + rootName.idea.slice(-1) : rootName.name;
  });
}
